/**
 * Area-mode global state.
 *
 * Area-mode is a *map state* — distinct from any tool. A polygon has been
 * drawn and the map is in a mode where the user can see the masts inside it,
 * organise lines, export the area, generate a capacity report, edit mastpoints
 * via the Toolbar, etc. This module owns the canonical state for that mode.
 *
 * Section info, load cases and the section calculation belong to path-mode —
 * see `path-mode-state.svelte.ts`.
 *
 * Lives in a `.svelte.ts` module so we can use Svelte 5 runes (`$state`,
 * `$derived`) at module scope. Components import `areaModeState` and read
 * properties directly — Svelte's compiler wires up fine-grained reactivity
 * automatically:
 *
 *     import { areaModeState } from '…/area-mode-state.svelte';
 *     const isActive = $derived(areaModeState.active);
 */

import geojsonArea from '@mapbox/geojson-area';
import type { Entity } from 'cesium';

import type { IGeoJsonPolygonModel } from '$lib/models/IGeoJsonPolygonModel';

class AreaModeState {
	/** True once a polygon is drawn and area-mode is the active map state. */
	active = $state(false);

	/** GeoJSON polygon (closed ring). */
	polygon = $state<IGeoJsonPolygonModel['polygon'] | null>(null);

	/** The Cesium polygon entity that's drawn on the map. */
	polygonEntity = $state<Entity | null>(null);

	/** Polygon boundary ring as [lng, lat] pairs (not closed). */
	polygonBoundariesLngLat = $state<[number, number][]>([]);

	/** Polygon area in m² (computed from boundaries via `@mapbox/geojson-area`). */
	polygonAreaInSquareMeters = $state(0);

	/**
	 * When `true`, the right-side panel is collapsed to a small floating chip.
	 * When `false`, the full `PolygonSelectionDetails` panel is shown.
	 */
	panelMinimized = $state(false);

	// ── Mutators ───────────────────────────────────────────────────────────

	/**
	 * Records the polygon boundary ring AND the resulting area in one call.
	 * Mirrors the old `mapToolsStore.setPolygonAreaInSquareMetersBasedOnPolygonBoundaries`
	 * + `setPolygonBoundariesLngLat` pair.
	 */
	setBoundariesAndArea(polygonBoundariesLngLat: [number, number][]): void {
		this.polygonBoundariesLngLat = polygonBoundariesLngLat;
		const area = geojsonArea.geometry({
			type: 'Polygon',
			coordinates: [polygonBoundariesLngLat]
		});
		// Two decimal places (the `* 100 / 100` is a no-op carried from the
		// pre-migration helper — left in for parity).
		this.polygonAreaInSquareMeters = Math.ceil((area * 100) / 100);
	}

	/** Minimises the right-side panel to the chip. */
	minimize(): void {
		this.panelMinimized = true;
	}

	/** Restores the right-side panel from chip. */
	maximize(): void {
		this.panelMinimized = false;
	}

	/** Resets every field to its default — called by `deactivateAreaMode`. */
	reset(): void {
		this.active = false;
		this.polygon = null;
		this.polygonEntity = null;
		this.polygonBoundariesLngLat = [];
		this.polygonAreaInSquareMeters = 0;
		this.panelMinimized = false;
	}
}

export const areaModeState = new AreaModeState();
